import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFilters } from './hooks'
import {
  formatQuestionOptionsBySection,
  formatQuestionValueOptions,
  formatVariableOptions,
  findQuestionById,
  formatVariableValues
} from './utils'
import { QuestionFiltersSection, VariableFiltersSection, FilterHeader, FilterFooter } from './components'

const FiltersSidebar = ({
  currentFilters,
  onApplyFilters,
  onClose,
  isOpen,
  collect
}) => {
  const [showQuestionsSection, setShowQuestionsSection] = useState(true)
  const [showVariablesSection, setShowVariablesSection] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [serverError, setServerError] = useState(null)

  const {
    filters,
    fieldErrors,
    variableValues,
    activeQuestionFilters,
    activeVariableFilters,
    hasFiltersToDestroy,
    validateFilters,
    addQuestionFilter,
    removeQuestionFilter,
    updateQuestionFilter,
    updateQuestionValues,
    addVariableFilter,
    removeVariableFilter,
    updateVariableFilter,
    updateVariableValues,
    clearAllFilters,
    loadVariableValues
  } = useFilters(collect, currentFilters)

  useEffect(() => {
    if (isOpen) {
      const { scrollY } = window

      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflowY = 'hidden'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''

      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
    }

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen)
      setServerError(null)
  }, [isOpen])

  const questions = useMemo(() => {
    if (!collect?.sections)
      return []
    return collect.sections.reduce((acc, section) => [...acc, ...section.questions], [])
  }, [collect?.sections])

  const questionOptions = useMemo(() => formatQuestionOptionsBySection(
    collect?.sections || [], activeQuestionFilters.map(filter => filter.questionId?.toString())
  ), [collect?.sections, activeQuestionFilters])

  const variableOptions = useMemo(() => formatVariableOptions(
    collect?.variables || [], activeVariableFilters.map(filter => filter.variableId?.toString())
  ), [collect?.variables, activeVariableFilters])

  const getQuestionValueOptions = useCallback(questionId => {
    const question = findQuestionById(questionId, collect?.sections)
    if (question?.context === 'variable' && question?.variable_id)
      loadVariableValues(question.variable_id)
    return formatQuestionValueOptions(question, variableValues)
  }, [collect?.sections, loadVariableValues, variableValues])

  const getVariableValues = useCallback(variableId =>
    formatVariableValues(variableId, variableValues), [variableValues])

  const handleSubmit = useCallback(() => {
    if (validateFilters()) {
      setIsSubmitting(true)
      setServerError(null)

      onApplyFilters(filters).then(() => {
        setIsSubmitting(false)
        onClose()
      }).catch(() => {
        setIsSubmitting(false)
        setServerError('Une erreur est survenue lors de l\'application des filtres')
      })
    }
  }, [filters, validateFilters, onApplyFilters, onClose])

  const handleClearFilters = useCallback(() => {
    setIsClearing(true)
    setServerError(null)
    clearAllFilters()
  }, [clearAllFilters])

  useEffect(() => {
    const shouldSubmit = isClearing && (
      hasFiltersToDestroy ||
      activeQuestionFilters.length === 0 && activeVariableFilters.length === 0
    )
    if (shouldSubmit) {
      onApplyFilters(filters)
        .catch(() => {
          setServerError('Une erreur est survenue lors de la suppression des filtres')
        })
        .finally(() => {
          setIsClearing(false)
        })
    }
  }, [
    isClearing,
    filters,
    hasFiltersToDestroy,
    activeQuestionFilters.length,
    activeVariableFilters.length,
    onApplyFilters
  ])

  const handleQuestionChange = useCallback((uniqueId, value) =>
    updateQuestionFilter(uniqueId, 'questionId', value), [updateQuestionFilter])

  const toggleQuestionsSection = useCallback(() => {
    setShowQuestionsSection(prev => !prev)
  }, [])

  const toggleVariablesSection = useCallback(() => {
    setShowVariablesSection(prev => !prev)
  }, [])

  return (
    <>
      <div
        className={`modal-backdrop fade ${isOpen ? 'show' : ''}`}
        style={{ display: isOpen ? 'block' : 'none', zIndex: 1040 }}
        onClick={onClose}
      />
      <div
        className={`position-fixed h-100 bg-white ${isOpen ? 'show' : ''}`}
        style={{
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: '620px',
          transform: `translateX(${isOpen ? '0' : '100%'})`,
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <FilterHeader onClose={onClose} />
        <div className="flex-grow-1 overflow-auto">
          <div className="px-3 py-4">
            {Boolean(serverError) && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="fas fa-exclamation-triangle mr-2" />
                {serverError}
              </div>
            )}
            <QuestionFiltersSection
              showSection={showQuestionsSection}
              toggleSection={toggleQuestionsSection}
              activeFilters={activeQuestionFilters}
              questionOptions={questionOptions}
              valueOptions={getQuestionValueOptions}
              fieldErrors={fieldErrors}
              questions={questions}
              onAddFilter={addQuestionFilter}
              onRemoveFilter={removeQuestionFilter}
              onQuestionChange={handleQuestionChange}
              onValuesChange={updateQuestionValues}
            />
            <hr className="m-0 mt-3" />
            <VariableFiltersSection
              showSection={showVariablesSection}
              activeFilters={activeVariableFilters}
              variableOptions={variableOptions}
              valueOptions={getVariableValues}
              fieldErrors={fieldErrors}
              collect={collect}
              toggleSection={toggleVariablesSection}
              onAddFilter={addVariableFilter}
              onRemoveFilter={removeVariableFilter}
              onVariableChange={updateVariableFilter}
              onValuesChange={updateVariableValues}
            />
          </div>
        </div>
        <FilterFooter
          disabled={
            !hasFiltersToDestroy &&
            activeQuestionFilters.length === 0 &&
            activeVariableFilters.length === 0
          }
          isSubmitting={isSubmitting}
          isClearing={isClearing}
          onClearFilters={handleClearFilters}
          onApplyFilters={handleSubmit}
        />
      </div>
    </>
  )
}

FiltersSidebar.propTypes = {
  collect: PropTypes.shape({
    id: PropTypes.number.isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      questions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        context: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.number,
          content: PropTypes.string
        }))
      }))
    })),
    variables: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  }).isRequired,
  currentFilters: PropTypes.shape({
    questionFilters: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
      uniqueId: PropTypes.string
    })),
    variableFilters: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      variableId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      values: PropTypes.arrayOf(PropTypes.string),
      uniqueId: PropTypes.string
    }))
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default FiltersSidebar